import { useState, useEffect, useMemo, useCallback } from 'react';
import { Typography, message } from 'antd';
import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';
import { subscriptionApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { telegram } from '../../utils/telegram';
import SubscriptionSkeleton from './SubscriptionSkeleton';
import CurrentPlanHero from './CurrentPlanHero';
import DurationSwitcher from './DurationSwitcher';
import PlanCard from './PlanCard';
import InactiveSubscriptionBanner from './InactiveSubscriptionBanner';
import type { SubscriptionPlansResponse } from '../../types/business';

const { Text } = Typography;

/**
 * Subscription tab orchestrator — BE-driven data model.
 * Backend GET /v1/subscription-plans qaytaradigan SubscriptionPlansResponse'ni
 * to'g'ridan-to'g'ri iste'mol qiladi (tarif nomi, qulayliklar, prioritet,
 * narxlar, CTA holati — barchasi BE belgilaydi).
 */
export default function SubscriptionSection() {
  const { t, i18n } = useTranslation('owner');
  const { selectedBusiness, refreshSelectedBusiness } = useBusinessStore();
  const [data, setData] = useState<SubscriptionPlansResponse | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(1);

  const loadPlans = useCallback(() => {
    if (!selectedBusiness) return;
    subscriptionApi
      .getPlans(selectedBusiness.id)
      .then(setData)
      .catch((err: unknown) => {
        const e = err as { response?: { status?: number } };
        const status = e?.response?.status;
        if (status === 403) {
          void message.error(t('subscription.forbiddenError'));
        } else if (status === 404) {
          void message.error(t('subscription.tenantNotFoundError'));
        } else {
          void message.error(t('subscription.loadError'));
        }
      });
  }, [selectedBusiness, t]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Tarif nomlari va qulaylik matnlari BE'da Accept-Language bo'yicha
  // tarjima qilinadi — til o'zgarganda yangi til bilan qayta yuklanadi.
  useEffect(() => {
    const handler = () => { loadPlans(); };
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, [i18n, loadPlans]);

  // Joriy tarif uchun feature chips (hero strip uchun)
  const currentPlanFeatureChips = useMemo(() => {
    if (!data) return [];
    const currentPlan = data.plans.find((p) => p.code === data.currentPlanCode);
    if (!currentPlan) return [];
    return currentPlan.features
      .filter((f) => f.enabled)
      .map((f) => (f.value ? `${f.label} · ${f.value}` : f.label));
  }, [data]);

  if (!selectedBusiness) return null;
  if (!data) return <SubscriptionSkeleton />;

  const currentPlan = data.plans.find((p) => p.code === data.currentPlanCode);

  const handleCheckout = async (planCode: string) => {
    setCheckoutLoading(planCode);
    try {
      const result = await subscriptionApi.checkout(selectedBusiness.id, {
        plan: planCode,
        durationMonths: duration,
        returnUrl: window.location.origin,
      });
      telegram.hapticFeedback.notification('success');
      WebApp.openLink(result.checkoutUrl);
      setTimeout(() => void refreshSelectedBusiness(), 5000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; code?: string } } };
      const code = error?.response?.data?.code;
      if (code === 'ALREADY_ON_PLAN') {
        void message.info(t('subscription.alreadyOnPlan'));
      } else {
        void message.error(error?.response?.data?.message ?? t('subscription.checkoutError'));
      }
      telegram.hapticFeedback.notification('error');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const isInactive = data.currentPlanStatus === 'INACTIVE';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* INACTIVE banner — grace ham tugagan, FREE'ga downgrade qilingan */}
      {isInactive && (
        <div className="bl-animate-in">
          <InactiveSubscriptionBanner />
        </div>
      )}

      {/* Current plan hero */}
      {currentPlan && (
        <div className="bl-animate-in">
          <CurrentPlanHero
            plan={currentPlan}
            showActiveBadge={data.showCurrentActiveBadge}
            featureChips={currentPlanFeatureChips}
            expiresAt={data.currentPlanExpiresAt ?? null}
            status={data.currentPlanStatus}
            graceUntil={data.currentPlanGraceUntil ?? null}
          />
        </div>
      )}

      {/* Duration switcher */}
      <div className="bl-animate-in bl-animate-in-delay-1">
        <DurationSwitcher
          value={duration}
          onChange={setDuration}
          options={data.durationOptions}
        />
      </div>

      {/* Section caption */}
      <div className="bl-animate-in bl-animate-in-delay-1" style={{ paddingLeft: 4, marginTop: 4 }}>
        <Text
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--bl-text-muted, #9ca3af)',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          {t('subscription.compareCaption')}
        </Text>
      </div>

      {/* Plan cards — BE priority bo'yicha tartiblangan */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {data.plans.map((plan, idx) => {
          const isCurrent = plan.code === data.currentPlanCode;
          const delayClass =
            idx === 0
              ? 'bl-animate-in bl-animate-in-delay-2'
              : idx === 1
                ? 'bl-animate-in bl-animate-in-delay-2'
                : 'bl-animate-in bl-animate-in-delay-3';
          return (
            <div key={plan.code} className={delayClass}>
              <PlanCard
                plan={plan}
                selectedDurationMonths={duration}
                isCurrent={isCurrent}
                checkoutLoading={checkoutLoading === plan.code}
                onCheckout={(code) => void handleCheckout(code)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
